import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import ShareButton from "@/components/ShareButton";
import {
  ArrowLeft, GraduationCap, Buildings, Clock, ArrowSquareOut, BookOpen, Certificate, SealCheck, LinkSimple,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function CoursePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => { api.get(`/courses/${id}`).then((r) => setC(r.data)).catch(() => setC(false)); }, [id]);
  useSeo(c && c !== false ? `${c.name} — ${c.institution_name}` : "Course",
    c && c !== false ? (c.description || `${c.degree_type} program at ${c.institution_name}.`) : null);
  if (c === null) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (c === false) return <div className="p-12 text-center text-muted-foreground">Course not found.</div>;

  const meta = [
    ["Department", c.department], ["Faculty", c.faculty], ["Degree type", c.degree_type],
    ["Duration", c.duration], ["Availability", c.availability],
  ].filter(([, v]) => v);

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Back</button>

      <Card className="overflow-hidden border-border/60 shadow-sm">
        <div className="relative bg-primary p-8 text-primary-foreground grain">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <BookOpen size={22} weight="duotone" />
                {c.verified ? <Badge className="rounded-full bg-white/20 font-normal backdrop-blur gap-1"><SealCheck size={12} weight="fill" /> Verified</Badge>
                  : <Badge className="rounded-full bg-white/15 font-normal backdrop-blur">Community</Badge>}
              </div>
              <h1 className="mt-3 font-heading text-3xl font-medium leading-tight">{c.name}</h1>
              <Link to={`/institutions/${c.institution_id}`} className="mt-2 inline-flex items-center gap-1.5 text-primary-foreground/80 hover:text-primary-foreground">
                <Buildings size={16} /> {c.institution_name}
              </Link>
            </div>
            <ShareButton targetPath={`/courses/${c.public_slug || c.id}`} entityType="course" entityId={c.id} title={c.name} variant="secondary" />
          </div>
        </div>

        <div className="p-6 sm:p-8">
          {meta.length > 0 && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {meta.map(([label, value]) => (
                <div key={label} className="rounded-xl border border-border/60 p-4">
                  <div className="overline text-muted-foreground">{label}</div>
                  <div className="mt-1 font-medium capitalize">{value}</div>
                </div>
              ))}
            </div>
          )}
          {c.description && <p className="mt-6 leading-relaxed text-foreground/90">{c.description}</p>}

          <div className="mt-6 flex flex-wrap gap-3">
            {c.official_url && (
              <a href={c.official_url} target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full gap-2" data-testid="course-official-link"><ArrowSquareOut size={16} /> Official Course Info</Button>
              </a>
            )}
            {c.admission_url && (
              <a href={c.admission_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full gap-2"><Certificate size={16} /> Admissions</Button>
              </a>
            )}
          </div>
        </div>
      </Card>

      {c.related?.length > 0 && (
        <Card className="mt-6 border-border/60 p-6 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-medium"><LinkSimple className="text-secondary" size={18} /> Related programs</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {c.related.map((rc) => (
              <Link key={rc.id} to={`/courses/${rc.public_slug || rc.id}`}>
                <Card className="border-border/60 p-4 transition-colors hover:border-secondary/40"><div className="font-medium">{rc.name}</div><div className="text-xs text-muted-foreground">{rc.degree_type}</div></Card>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </main>
  );
}
